<template>
    <div class="edit-profile">
        <div class="flex dashboard-wrapper">
            <div class="w-[15%] bg-slate-100">
                <SidebarDashboard/>
            </div>

            <div class="w-[85%]">
                <Header></Header>

                <div class="dashboard-content p-5 px-10 pb-20 min-h-screen bg-slate-50">
                    <h2 class="mb-6 font-bold text-3xl text-slate-800">{{ currentTitle }}</h2>

                    <div class="flex flex-col lg:flex-row gap-8 items-start">
                        <!-- Avatar Column -->
                        <div class="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center w-full lg:w-[320px]">
                            <p class="font-bold text-lg mb-4 text-slate-700">Foto de Perfil</p>
                            <div class="relative group">
                                <img 
                                    class="rounded-xl object-cover border border-slate-200 shadow-inner aspect-square w-[240px] h-[240px]" 
                                    :src="avatarPreviewUrl" 
                                    alt="Foto de Perfil"
                                >
                                <div class="absolute inset-0 bg-black bg-opacity-40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer" @click="triggerFileInput">
                                    <span class="text-white text-sm font-semibold flex items-center gap-2">
                                        <i class="pi pi-camera text-lg"></i> Alterar Foto
                                    </span>
                                </div>
                            </div>
                            <input 
                                type="file" 
                                ref="fileInput" 
                                class="hidden" 
                                accept="image/*" 
                                @change="onFileChange"
                            >
                            <button 
                                class="p-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white mt-4 rounded-lg w-full flex items-center justify-center gap-2 font-semibold transition-colors shadow-sm text-sm" 
                                @click="triggerFileInput"
                            >
                                <i class="pi pi-upload"></i> Selecionar Imagem
                            </button>
                            <p class="text-xs text-slate-400 mt-2 text-center">Formatos aceitos: JPG, PNG, WEBP. Máx: 2MB.</p>
                        </div>

                        <!-- Data Fields Column -->
                        <div class="grow bg-white p-8 rounded-xl shadow-sm border border-slate-100 w-full">
                            <p class="font-bold text-lg mb-6 text-slate-700">Dados Pessoais</p>

                            <FloatLabel class="mb-8">
                                <InputText class="p-3 border w-full rounded-lg" id="name" v-model="userData.name" />
                                <label for="name">Nome completo</label>
                            </FloatLabel>

                            <FloatLabel class="mb-8">
                                <InputText class="p-3 border w-full rounded-lg" id="email" v-model="userData.email" />
                                <label for="email">Endereço de E-mail</label>
                            </FloatLabel>

                            <FloatLabel class="mb-8">
                                <InputText class="p-3 border w-full rounded-lg" id="occupation" v-model="userData.occupation" />
                                <label for="occupation">Função / Cargo</label>
                            </FloatLabel>

                            <hr class="my-6 border-slate-100">
                            
                            <p class="font-bold text-lg mb-6 text-slate-700">Segurança</p>

                            <FloatLabel class="mb-4">
                                <InputText type="password" class="p-3 border w-full rounded-lg" id="password" v-model="password" />
                                <label for="password">Nova Senha (deixe vazio para não alterar)</label>
                            </FloatLabel>
                            <p class="text-xs text-slate-400 mb-6">A nova senha deve ter no mínimo 6 caracteres.</p>
                        </div>
                    </div>

                    <!-- Floating Action Button -->
                    <Button
                        icon="pi pi-save"
                        label="Salvar Perfil"
                        class="p-3 px-6 mt-10 bg-green-500 hover:bg-green-600 text-white rounded-lg fixed right-8 bottom-8 shadow-lg z-50 transition-colors font-semibold"
                        @click="saveProfile"
                    />
                </div>

                <Footer></Footer>
            </div>
        </div>
    </div>
</template>

<script>
import SidebarDashboard from '@/components/Dashboard/SidebarDashboard.vue'
import Header from '@/components/Dashboard/HeaderDashboard.vue'
import Footer from '@/components/Dashboard/FooterDashboard.vue'
import InputText from 'primevue/inputtext'
import FloatLabel from 'primevue/floatlabel'
import Button from 'primevue/button'
import { authStore } from '@/store/authStore'
import axios from 'axios'

export default {
    name: 'DashboardEditProfile',
    components: {
        SidebarDashboard, Header, Footer, InputText, FloatLabel, Button
    },
    data() {
        return {
            currentTitle: "",
            userData: {
                avatar: "",
                name: "",
                occupation: "",
                email: ""
            },
            password: "",
            avatarFile: null,
            avatarPreview: null
        }
    },
    computed: {
        avatarPreviewUrl() {
            if (this.avatarPreview) {
                return this.avatarPreview
            }
            return this.userData.avatar || '/profile.jpg'
        }
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click()
        },
        onFileChange(e) {
            const file = e.target.files[0]
            if (file) {
                // Size validation: 2MB limit
                if (file.size > 2 * 1024 * 1024) {
                    this.$swal.fire('Arquivo muito grande', 'A imagem deve ter no máximo 2MB.', 'error')
                    return
                }
                this.avatarFile = file
                this.avatarPreview = URL.createObjectURL(file)
            }
        },
        saveProfile() {
            if (!this.userData.name || !this.userData.name.trim()) {
                this.$swal.fire('Nome Obrigatório', 'Por favor insira seu nome.', 'warning')
                return
            }
            if (!this.userData.email || !this.userData.email.trim()) {
                this.$swal.fire('E-mail Obrigatório', 'Por favor insira seu endereço de e-mail.', 'warning')
                return
            }
            if (this.password && this.password.length < 6) {
                this.$swal.fire('Senha Curta', 'A nova senha deve ter pelo menos 6 caracteres.', 'warning')
                return
            }

            this.$swal.fire({
                title: 'Salvando Perfil...',
                html: 'Aguarde um momento...',
                didOpen: () => {
                    this.$swal.showLoading()
                }
            })

            const auth = authStore()
            const formData = new FormData()
            formData.append('name', this.userData.name)
            formData.append('email', this.userData.email)
            if (this.avatarFile) {
                formData.append('avatar_file', this.avatarFile)
            }
            if (this.password) {
                formData.append('password', this.password)
            }

            axios.post('/user/update-profile', formData, {
                headers: {
                    'Authorization': `Bearer ${auth.token}`,
                    'Content-Type': 'multipart/form-data'
                }
            }).then((response) => {
                this.$swal.close()
                if (response.data && response.data.status === 200) {
                    const newUserData = {
                        ...auth.userData,
                        name: response.data.userData.name,
                        email: response.data.userData.email,
                        avatar: response.data.userData.avatar,
                        occupation: this.userData.occupation
                    }
                    auth.setUserData(newUserData)
                    this.userData.avatar = response.data.userData.avatar
                    this.avatarPreview = null
                    this.password = ""
                    this.$swal.fire('Perfil Salvo!', 'Suas alterações foram salvas com sucesso.', 'success')
                } else {
                    this.$swal.fire('Erro ao Salvar', response.data.message || 'Erro inesperado.', 'error')
                }
            }).catch(e => {
                this.$swal.close()
                console.error(e)
                const errorMsg = e.response?.data?.message || 'Erro ao conectar ao servidor.'
                this.$swal.fire('Erro ao Salvar', errorMsg, 'error')
            })
        }
    },
    mounted() {
        this.currentTitle = this.$route.meta.title
        this.$watch("$route", () => {
            this.currentTitle = this.$route.meta.title;
        });

        const auth = authStore()
        const userData = auth.userData

        if (userData) {
            this.userData = {
                avatar: userData.avatar || "",
                name: userData.name || "",
                occupation: userData.occupation || "",
                email: userData.email || ""
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.edit-profile {
    .dashboard-wrapper {
        min-height: 100vh;
    }
}
</style>